import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, Package, CheckCircle2, Clock, Truck, AlertCircle, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useLang } from "@/context/LangContext";

const SYSTEM_EN = { "Création automatique": "Created automatically", "Mise à jour initiale": "Initial update", "Système": "System" };
const STATUS_EN = { "En attente": "Pending", "En cours": "In transit", "Livré": "Delivered", "Litige": "Issue" };

const STATUS_META = {
    "En attente": { icon: Clock, cls: "chip-attente", step: 1 },
    "En cours":   { icon: Truck, cls: "chip-cours", step: 2 },
    "Livré":      { icon: CheckCircle2, cls: "chip-livre", step: 3 },
    "Litige":     { icon: AlertCircle, cls: "chip-litige", step: 0 },
};

export default function SuiviPage() {
    const { t, lang } = useLang();
    const st = (s) => (lang === "en" ? STATUS_EN[s] || s : s);
    const locale = lang === "en" ? "en-GB" : "fr-FR";
    const [params, setParams] = useSearchParams();
    const [ref, setRef] = useState(params.get("ref") || "");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const search = async (r) => {
        if (!r) return;
        setLoading(true);
        setError("");
        setData(null);
        try {
            const { data } = await api.get(`/public/track/${encodeURIComponent(r)}`);
            setData(data);
        } catch (err) {
            setError(err.response?.status === 404 ? "notfound" : "other");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (params.get("ref")) search(params.get("ref"));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const submit = (e) => {
        e.preventDefault();
        const r = ref.trim().toUpperCase();
        setParams({ ref: r });
        search(r);
    };

    return (
        <section className="bg-[#f4f6fa] min-h-[calc(100vh-72px)] py-16 lg:py-24">
            <div className="max-w-4xl mx-auto px-5 lg:px-8">
                <div className="text-xs uppercase tracking-[0.3em] text-orange-400 font-semibold">{t("Portail client", "Client portal")}</div>
                <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight text-white mt-3">
                    {t("Suivi d'expédition.", "Shipment tracking.")}
                </h1>
                <p className="text-slate-400 mt-4">
                    {t("Saisissez votre référence d'ordre de transport (ex. ", "Enter your transport order reference (e.g. ")}<span className="font-mono text-slate-200">OT-2026-0001</span>{t(") pour consulter l'avancement.", ") to check its progress.")}
                </p>

                <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                        <input
                            data-testid="suivi-input"
                            value={ref}
                            onChange={(e) => setRef(e.target.value)}
                            placeholder="OT-2026-0001"
                            className="w-full pl-11 pr-4 py-3.5 rounded-full bg-white/10 border border-white/15 text-white placeholder:text-slate-400 focus:outline-none focus:border-orange-400 font-mono text-sm"
                        />
                    </div>
                    <button type="submit" data-testid="suivi-submit-button" className="kz-btn-primary justify-center">
                        {loading ? <Loader2 className="animate-spin" size={16} /> : <Search size={16} />} {t("Rechercher", "Search")}
                    </button>
                </form>

                {error && (
                    <div data-testid="suivi-error" className="mt-8 rounded-xl border border-red-500/30 bg-red-500/10 p-5 text-red-200 flex items-center gap-3">
                        <AlertCircle size={18} /> {error === "notfound" ? t("Aucune expédition trouvée avec cette référence", "No shipment found with this reference") : t("Une erreur est survenue, veuillez réessayer.", "Something went wrong, please try again.")}
                    </div>
                )}

                {data && (
                    <div data-testid="suivi-result" className="mt-10 rounded-2xl border border-white/10 bg-[#111c38] p-6 lg:p-10">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="text-xs uppercase tracking-widest text-slate-400">{t("Référence", "Reference")}</div>
                                <div className="font-mono text-lg lg:text-xl text-white mt-1">{data.reference}</div>
                                <div className="text-sm text-slate-300 mt-3">
                                    <span className="text-slate-500">{t("Client :", "Client:")}</span> {data.client_name}
                                </div>
                                <div className="text-sm text-slate-300 mt-1">
                                    <span className="text-slate-500">{t("Marchandise :", "Goods:")}</span> {data.goods_description}
                                </div>
                            </div>
                            {(() => {
                                const meta = STATUS_META[data.status] || STATUS_META["En attente"];
                                const Icon = meta.icon;
                                return (
                                    <span className={`chip ${meta.cls} text-sm px-4 py-1.5`}>
                                        <Icon size={14} /> {st(data.status)}
                                    </span>
                                );
                            })()}
                        </div>

                        <div className="grid sm:grid-cols-3 gap-5 mt-8 pt-6 border-t border-white/10">
                            <Info label={t("Départ", "Departure")} value={`${data.pickup_city}${data.pickup_country ? `, ${data.pickup_country}` : ""}`} sub={data.pickup_date} />
                            <Info label={t("Arrivée", "Arrival")} value={`${data.delivery_city}${data.delivery_country ? `, ${data.delivery_country}` : ""}`} sub={data.delivery_date} />
                            <Info label={t("Mise à jour", "Last update")} value={new Date(data.updated_at).toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" })} />
                        </div>

                        {/* progress */}
                        {data.status !== "Litige" && (
                            <div className="mt-10">
                                <div className="flex items-center justify-between">
                                    {[
                                        { label: t("Pris en charge", "Collected"), key: 1 },
                                        { label: t("En transit", "In transit"), key: 2 },
                                        { label: t("Livré", "Delivered"), key: 3 },
                                    ].map((s, i, arr) => {
                                        const current = (STATUS_META[data.status]?.step) || 1;
                                        const active = s.key <= current;
                                        return (
                                            <React.Fragment key={s.key}>
                                                <div className="flex flex-col items-center">
                                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 ${active ? "bg-orange-400 border-orange-400 text-[#0b1329]" : "bg-transparent border-white/20 text-slate-500"}`}>
                                                        {active ? <CheckCircle2 size={18} /> : <Package size={18} />}
                                                    </div>
                                                    <div className={`text-xs mt-2 ${active ? "text-white" : "text-slate-500"}`}>{s.label}</div>
                                                </div>
                                                {i < arr.length - 1 && (
                                                    <div className={`flex-1 h-0.5 mx-2 ${s.key < current ? "bg-orange-400" : "bg-white/15"}`} />
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        )}

                        {/* timeline */}
                        <div className="mt-10">
                            <div className="text-xs uppercase tracking-widest text-slate-400 mb-4">{t("Historique", "History")}</div>
                            <ol className="relative border-l border-white/15 ml-2 space-y-5">
                                {[...(data.status_history || [])].reverse().map((h, idx) => (
                                    <li key={idx} className="ml-4">
                                        <span className="absolute -left-1.5 w-3 h-3 rounded-full bg-orange-400 mt-1.5" />
                                        <div className="flex items-center gap-3 flex-wrap">
                                            <span className={`chip ${STATUS_META[h.status]?.cls || "chip-emise"} text-[11px]`}>{st(h.status)}</span>
                                            <span className="text-xs text-slate-500 font-mono">{new Date(h.at).toLocaleString(locale)}</span>
                                            {h.by && <span className="text-xs text-slate-500">· {lang === "en" ? SYSTEM_EN[h.by] || h.by : h.by}</span>}
                                        </div>
                                        {h.comment && <div className="text-sm text-slate-300 mt-1.5">{lang === "en" ? SYSTEM_EN[h.comment] || h.comment : h.comment}</div>}
                                    </li>
                                ))}
                            </ol>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
}

function Info({ label, value, sub }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500">{label}</div>
            <div className="text-sm text-slate-100 mt-1">{value}</div>
            {sub && <div className="text-xs text-slate-500 mt-0.5 font-mono">{sub}</div>}
        </div>
    );
}
